import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { UrlService } from '../../../../environments/urlService';
import { ResetAllDataService } from './reset-all-data.service';
import { LoginResponse } from '../../login/component/dto/login.dto';
import { IS_SPINNER_ACTIVE } from '../../../util/interceptors';
import { firstValueFrom, Observable } from 'rxjs';
import { ForgotPasswordResponse } from '../dto/ForgotPasswordDto';
import { BasicErrorDto } from '../dto/BasicErrorDto';
import { Router } from "@angular/router";
import { SessionExpirationService } from "./session-expiration.service";

@Injectable({
  providedIn: 'root',
})
export class AuthBackendService {
  private readonly url: string;

  constructor(
    private http: HttpClient,
    private urlService: UrlService,
    private resetService: ResetAllDataService,
    private router: Router,
  ) {
    this.url = this.urlService.getFmsUrl();
  }

  login(login: string, password: string, captchaV3Token: string, captchaV2Token: string | null): Promise<LoginResponse> {
    return this.http.post<LoginResponse>(
      this.url + 'auth/login',
      { login, password, captchaV3Token, captchaV2Token },
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true, responseType: 'json',
      },
    ).toPromise();
  }

  getMaintenance(): Promise<boolean> {
    return this.http.get<boolean>(
      this.url + 'auth/maintenance',
      { withCredentials: true, responseType: 'json' },
    ).toPromise();
  }

  async logout(): Promise<void> {
    SessionExpirationService.setSessionLeaderByLogout();
    await firstValueFrom(this.removeCookie());
    this.resetService.reset();
    await this.router.navigate(['/']);
  }

  removeCookie(): Observable<void> {
    return this.http.post<void>(this.url + 'logout/cookies', null, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  changePassword(c: { oldPassword: string; newPassword: string }): Promise<void> {
    return this.http.post<void>(this.url + 'user/password', c,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true, responseType: 'json',
      }).toPromise();
  }


  /**
   * Requests password recovery email.
   *
   * @param email User's email
   * @param captchaTokenV2 captcha v2 token - optional
   * @param captchaTokenV3 captcha v3 token - mandatory, if captcha enabled
   * @returns message that email was sent if all is ok
   */
  forgotPasswordCaptcha(email: string, captchaTokenV2: string, captchaTokenV3: string): Promise<ForgotPasswordResponse | BasicErrorDto> {
    const body = {
      email,
      captchaTokenV2,
      captchaTokenV3,
    };
    return firstValueFrom(this.http.post<ForgotPasswordResponse>(
      this.url + 'auth/forgotpassword', body,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
        responseType: 'json',
      },
    ));
  }

  /**
   * Checks if the reset password token is valid. It is valid if it exists and has not expired.
   *
   * @param token Password reset token
   * @returns true if token is valid.
   */
  checkPasswordResetToken(token: string): Observable<boolean> {
    return this.http.get<boolean>(
      this.url + 'auth/new-password/' + token,
      { withCredentials: true, responseType: 'json' },
    );
  }

  /**
   * Sets new password for user by password reset token.
   *
   * @param token Password reset token
   * @param password New password
   * @returns message that password was set if all is ok
   */
  setNewPassword(token: string, password: string): Promise<void> {
    return this.http.post<void>(
      this.url + 'auth/new-password/' + token, password,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true, responseType: 'json',
      },
    ).toPromise();
  }

  checkRecoveryAccess(): Promise<void> {
    return this.http.get<void>(String(this.url + 'recovery'), {
      withCredentials: true,
      responseType: 'json',
    }).toPromise();
  }

  getAgreementStatus(token: string): Observable<boolean> {
    return this.http.get<boolean>(
      this.url + 'auth/get-agreement/' + token,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      });
  }
}
